import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi, notesApi } from "../../../api";
import Note, {
  FullNote,
  LienPosition,
  NoteStatus,
  PropertyImage,
} from "../../../interfaces/mortgageNote";
import inputFields from "./inputFields";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import ImageUpload from "../../../components/admin/ImageUpload";
import { Reorder } from "framer-motion";

export interface EditNoteParams {
  loanNumber: string;
  isPerforming: boolean;
  status: string;
  loanType: string;
  lienPosition: string;
  loanAmount: string;
  purchasePrice: string;
  marketValue: string;
  downPayment: string;
  ltv: string;
  rateOfReturn: string;
  loanTerm: string;
  useOfProceeds: string;
  exitStrategy: string;
  maturityDate: string;
  averageCredit: string;
  averageBankBalance: string;
  repeatBorrower: boolean;
  paidOffPreviously: boolean;
  amountHeld: string;
  interestRate: string;
}

// Define which fields are editable
const editableFields = [
  "loanNumber",
  "isPerforming",
  "status",
  "loanType",
  "lienPosition",
  "loanAmount",
  "purchasePrice",
  "marketValue",
  "downPayment",
  "ltv",
  "rateOfReturn",
  "loanTerm",
  "useOfProceeds",
  "exitStrategy",
  "maturityDate",
  "averageCredit",
  "averageBankBalance",
  "repeatBorrower",
  "paidOffPreviously",
  "amountHeld",
  "interestRate",
];

const transformFullNoteToFormData = (note: FullNote): EditNoteParams => {
  return {
    loanNumber: note.loan_number ?? "",
    isPerforming: note.is_performing ?? false,
    status: note.status ?? "",
    loanType: note.loan_type ?? "",
    lienPosition: note.lien_position?.toString() ?? "",
    loanAmount: note.loan_amount?.toString() ?? "",
    purchasePrice: note.purchase_price?.toString() ?? "",
    marketValue: note.appraised_value?.toString() ?? "",
    downPayment: note.down_payment?.toString() ?? "",
    ltv: note.loan_to_value?.toString() ?? "",
    rateOfReturn: note.interest_rate?.toString() ?? "",
    loanTerm: note.loan_term?.toString() ?? "",
    useOfProceeds: note.use_of_proceeds ?? "",
    exitStrategy: note.exit_strategy ?? "",
    maturityDate: note.maturity_date ?? "",
    averageCredit: note.average_credit?.toString() ?? "",
    averageBankBalance: note.average_balance?.toString() ?? "",
    repeatBorrower: note.repeat_borrower ?? false,
    paidOffPreviously: note.previous_cogo ?? false,
    amountHeld: note.funds_held_in_escrow?.toString() ?? "",
    interestRate: note.interest_rate?.toString() ?? "",
  };
};

const transformFormDataToFullNote = (
  formData: EditNoteParams
): Partial<Note> => {
  return {
    loan_type: formData.loanType,
    loan_amount: Number(formData.loanAmount),
    purchase_price: Number(formData.purchasePrice),
    appraised_value: Number(formData.marketValue),
    loan_to_value: Number(formData.ltv),
    loan_term: Number(formData.loanTerm),
    down_payment: Number(formData.downPayment),
    exit_strategy: formData.exitStrategy,
    status: formData.status as NoteStatus,
    loan_number: formData.loanNumber,
    use_of_proceeds: formData.useOfProceeds,
    maturity_date: formData.maturityDate,
    lien_position: formData.lienPosition as LienPosition,
    is_performing: formData.isPerforming,
    average_credit: Number(formData.averageCredit),
    average_balance: Number(formData.averageBankBalance),
    repeat_borrower: formData.repeatBorrower,
    previous_cogo: formData.paidOffPreviously,
    funds_held_in_escrow: Number(formData.amountHeld),
    interest_rate: Number(formData.interestRate),
  };
};

interface UploadImage {
  id?: number;
  file?: File;
  link?: string;
  alt: string;
  fileName: string;
  order_position: number;
  tag_text?: string;
  tag_color?: string;
}

const Edit = () => {
  const { noteID } = useParams<{ noteID: string }>();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<EditNoteParams | null>(null);
  const [images, setImages] = useState<UploadImage[]>([]);

  useEffect(() => {
    const fetchNote = async () => {
      try {
        setIsLoading(true);
        const note: FullNote = await notesApi.fetchNote(Number(noteID));
        setFormData(transformFullNoteToFormData(note));
        // Transform PropertyImage to UploadImage and sort by order_position
        setImages(
          (note.images || [])
            .map((img) => ({
              id: img.id,
              link: img.link,
              alt: img.name,
              fileName: img.name,
              order_position: img.order_position,
              tag_text: img.tag_text,
              tag_color: img.tag_color,
            }))
            .sort((a, b) => a.order_position - b.order_position)
        );
      } catch (error) {
        console.error("Error fetching note:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (noteID) {
      fetchNote();
    }
  }, [noteID]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    const fieldConfig = inputFields.find((field) => field.name === name);
    let newValue = value;

    if (fieldConfig?.inputRestriction === "number") {
      newValue = value.replace(/\D/g, "");
    } else if (fieldConfig?.inputRestriction === "text") {
      newValue = value.replace(/[^\w\s]/gi, "");
    }

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            [name]:
              type === "checkbox"
                ? (e.target as HTMLInputElement).checked
                : newValue,
          }
        : null
    );

    if (fieldConfig?.format) {
      (e.target as HTMLInputElement).value = fieldConfig.format(newValue);
    }
  };

  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setImages((prev) => {
        const newImages = Array.from(e.target.files!).map((file, index) => ({
          file,
          alt: "",
          fileName: file?.name || "",
          order_position: prev.length + index, // Start from the end of existing images
        }));

        const allImages = [...prev, ...newImages];
        return allImages.map((img, index) => ({
          ...img,
          order_position: index,
        }));
      });
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAltChange = (index: number, alt: string) => {
    setImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, alt } : img))
    );
  };

  const handleTagChange = (
    index: number,
    tag_text: string,
    tag_color: string
  ) => {
    setImages((prev) =>
      prev.map((img, i) =>
        i === index ? { ...img, tag_text, tag_color } : img
      )
    );
  };

  const handleReorderImages = (newOrder: UploadImage[]) => {
    // Update order_position for each image based on its new position
    const updatedOrder = newOrder.map((img, index) => ({
      ...img,
      order_position: index,
    }));
    setImages(updatedOrder);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    const transformedData = transformFormDataToFullNote(formData);
    try {
      setIsSaving(true);

      // Create FormData for the request
      const formDataToSend = new FormData();

      // Add the transformed note data
      Object.entries(transformedData).forEach(([key, value]) => {
        if (value !== undefined) {
          formDataToSend.append(key, String(value));
        }
      });

      // Separate new and existing images
      const newImages = images.filter((img) => !img.id);
      const existingImages = images.filter((img) => img.id);

      // Ensure all images have the correct order_position
      const allImages = [...existingImages, ...newImages].map((img, index) => ({
        ...img,
        order_position: index,
      }));

      // Create metadata for both new and existing files
      const imageMetadata = {
        ...existingImages.reduce(
          (acc, img) => ({
            ...acc,
            [img.id!.toString()]: {
              alt: img.alt,
              existing: true,
              url: (img.link || "").split("?")[0],
              order_position: img.order_position,
              tag_text: img.tag_text,
              tag_color: img.tag_color,
            },
          }),
          {}
        ),
        ...newImages.reduce(
          (acc, img) => ({
            ...acc,
            [img.file?.name || img.fileName]: {
              alt: img.alt,
              existing: false,
              order_position: img.order_position,
              tag_text: img.tag_text,
              tag_color: img.tag_color,
            },
          }),
          {}
        ),
      };

      // Add new images to FormData
      newImages.forEach((image) => {
        if (image.file) {
          formDataToSend.append("images", image.file);
        }
      });

      // Add image metadata
      formDataToSend.append("image_metadata", JSON.stringify(imageMetadata));

      // Send the request
      await adminApi.updateNote(Number(noteID), formDataToSend);
      navigate(`/notes/${noteID}`);
    } catch (error) {
      console.error("Error updating note:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || isSaving) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="flex items-center justify-center gap-4 mt-60 bg-base-200 p-4 rounded-lg">
          <LoadingSpinner />
          <span className="text-2xl font-bold animate-pulse">
            {isSaving ? "Saving Note..." : "Loading Note..."}
          </span>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p>Error loading note data</p>
      </div>
    );
  }

  // Filter input fields to only show editable fields
  const filteredInputFields = inputFields.filter((field) =>
    editableFields.includes(field.name)
  );

  return (
    <div className="w-full flex items-center justify-center flex-col columns-1">
      <h1 className="text-[40px] py-4">Edit Note</h1>
      <form onSubmit={handleSubmit} className="flex flex-col columns-1 gap-3">
        {filteredInputFields.map((field) => (
          <div
            key={field.name}
            className={`form-group flex ${
              field.type === "checkbox"
                ? "flex-row justify-between py-2"
                : "flex-col"
            } columns-1 gap-1`}
          >
            <label htmlFor={field.name} className="font-bold text-white">
              {field.label}
            </label>
            {field.type === "select" ? (
              <select
                id={field.name}
                name={field.name}
                value={(formData as any)[field.name]}
                onChange={handleInputChange}
                className="select select-bordered bg-base-200"
              >
                {field.options?.map((option) => {
                  if (typeof option === "string") {
                    return (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    );
                  } else if (typeof option === "object") {
                    return (
                      <option
                        key={`${option.value}-${option.name}`}
                        value={option.value}
                      >
                        {option.name}
                      </option>
                    );
                  }
                  return null;
                })}
              </select>
            ) : field.type === "textarea" ? (
              <textarea
                id={field.name}
                name={field.name}
                value={(formData as any)[field.name]}
                onChange={handleInputChange}
                className="textarea textarea-bordered bg-base-200"
                data-1p-ignore
              />
            ) : (
              <input
                type={field.type}
                id={field.name}
                name={field.name}
                value={
                  field.type !== "file" && field.type !== "checkbox"
                    ? (formData as any)[field.name]
                    : undefined
                }
                checked={
                  field.type === "checkbox"
                    ? (formData as any)[field.name]
                    : undefined
                }
                onChange={handleInputChange}
                className={`${
                  field.type === "checkbox"
                    ? "checkbox"
                    : field.type === "file"
                    ? "file-input bg-base-200 file-input-bordered file-input-info"
                    : "input input-bordered bg-base-200"
                }`}
                data-1p-ignore
              />
            )}
          </div>
        ))}

        {/* Property Images section */}
        <div className="form-group flex flex-col columns-1 gap-1 mt-4">
          <label className="font-bold text-white">Property Images</label>
          <div className="flex gap-2">
            <input
              type="file"
              onChange={handleAddImage}
              className="file-input file-input-bordered file-input-info w-full"
              multiple
              accept="image/*"
            />
          </div>
        </div>

        {/* Display uploaded images */}
        <div className="mt-4">
          <h3 className="font-bold text-white mb-2">Images:</h3>
          <Reorder.Group
            axis="y"
            values={images}
            onReorder={handleReorderImages}
            className="space-y-2"
          >
            {images.map((image, index) => (
              <ImageUpload
                key={image.id || image.fileName}
                image={image}
                index={index}
                onRemove={handleRemoveImage}
                onAltChange={handleAltChange}
                onTagChange={handleTagChange}
              />
            ))}
          </Reorder.Group>
        </div>

        <button
          type="submit"
          className="btn btn-info text-white font-bold text-xl mb-4 mt-8"
        >
          {isLoading ? (
            <LoadingSpinner className="w-5 h-5" />
          ) : (
            <span>Update Note</span>
          )}
        </button>
      </form>
    </div>
  );
};

export default Edit;
